import { Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { DropdownModule } from "primeng/dropdown";
import { ButtonModule } from "primeng/button";
import { ToastModule } from "primeng/toast";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { InputTextModule } from "primeng/inputtext";
import { CardModule } from "primeng/card";
import { TabViewModule } from "primeng/tabview";
import { TableModule } from "primeng/table";
import { DialogModule } from "primeng/dialog";
import { AutoCompleteModule } from "primeng/autocomplete";
import { MessageService } from "primeng/api";
import { CourtService } from "../../services/court.service";
import { CourtReservationService } from "../../services/court-reservation.service";
import { AuthenticationService } from "../../services/authentication.service";
import { UserService } from "../../services/user.service";
import { firstValueFrom } from "rxjs";

@Component({
  selector: "app-fixed-booking",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DropdownModule,
    ButtonModule,
    ToastModule,
    ProgressSpinnerModule,
    InputTextModule,
    CardModule,
    TabViewModule,
    TableModule,
    DialogModule,
    AutoCompleteModule,
  ],
  providers: [MessageService],
  templateUrl: "./fixed-booking.component.html",
  styleUrl: "./fixed-booking.component.css",
})
export class FixedBookingComponent implements OnInit {
  username = "";
  allUsers: any[] = [];
  filteredUsers: string[] = [];
  days = [
    { label: "Sunday", value: 0 },
    { label: "Monday", value: 1 },
    { label: "Tuesday", value: 2 },
    { label: "Wednesday", value: 3 },
    { label: "Thursday", value: 4 },
    { label: "Friday", value: 5 },
    { label: "Saturday", value: 6 },
  ];
  times: { label: string; value: number }[] = [];
  monthsOptions = Array.from({ length: 19 }, (_, i) => ({
    label: `${i + 6}`,
    value: i + 6,
  }));

  selectedDay: number | null = null;
  selectedTime: number | null = null;
  selectedCourt: any = null;
  selectedMonths = 6;
  selectedDuration = 1;
  durationOptions = [
    { label: '1 hour', value: 1 },
    { label: '2 hours', value: 2 },
    { label: '3 hours', value: 3 },
  ];

  courts: any[] = [];
  club: any = null;
  unavailable: Date[] = [];
  checked = false;
  verified = true;
  loading = false;
  slotPrice = 0;
  totalPrice = 0;
  activeTabIndex = 0;
  fixedReservations: any[] = [];
  filteredReservations: any[] = [];
  searchTerm = "";
  individualReservations: any[] = [];
  filteredIndividualReservations: any[] = [];
  userFilter = "";
  seriesFilter = "";
  playerDialogVisible = false;
  selectedReservation: any = null;
  newPlayer: any = null;
  playerSuggestions: any[] = [];
  currentUser: any;
  infoDialogVisible = false;

  constructor(
    private courtService: CourtService,
    private reservationService: CourtReservationService,
    private auth: AuthenticationService,
    private userService: UserService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    const current = this.auth.currentUserValue;
    this.currentUser = current;
    if (!current) {
      this.verified = false;
      return;
    }
    this.userService.getAdminUser(current.id).subscribe({
      next: (u) => {
        this.verified = u.roles.find((x: { id: number }) => x.id == 2);
        if (this.verified) {
          this.club = u.club;
          if (this.club) {
            this.courtService
              .getCourtsForClub(this.club.id)
              .subscribe((res) => (this.courts = res));
            this.loadUsers();
            this.generateTimes();
          }
        }
      },
      error: () => {
        this.verified = false;
      },
    });
  }

  private loadUsers() {
    this.userService.getUsers().subscribe((res) => {
      this.allUsers = res;
    });
  }

  onTabChange(event: any) {
    if (event.index === 1 || event.index === 2) {
      this.loadFixedReservations();
    }
  }

  private loadFixedReservations() {
    if (!this.club) return;
    this.reservationService
      .getFixedReservationsForClub(this.club.id)
      .subscribe((res) => {
        const groups = new Map<any, any[]>();
        (res || []).forEach((r: any) => {
          const id = r.fixed_series_id;
          if (id == null) return;
          r.courtName = this.getCourtName(r.court_id);
          if (!groups.has(id)) groups.set(id, []);
          groups.get(id)!.push(r);
        });

        this.fixedReservations = [];
        this.individualReservations = [];

        groups.forEach((list) => {
          list.sort(
            (a: any, b: any) =>
              new Date(a.reservation_start_time).getTime() -
              new Date(b.reservation_start_time).getTime()
          );
          const start = new Date(list[0].reservation_start_time);
          const end = new Date(list[list.length - 1].reservation_end_time);
          const durationMonths =
            (end.getFullYear() - start.getFullYear()) * 12 +
            (end.getMonth() - start.getMonth());
          const sample = {
            ...list[0],
            fixedStart: start,
            fixedEnd: end,
            durationMonths,
            courtName: list[0].courtName,
          };
          this.fixedReservations.push(sample);
          list.forEach((r: any) => {
            r.fixedStart = start;
            r.fixedEnd = end;
            r.durationMonths = durationMonths;
            this.individualReservations.push(r);
          });
        });
        this.applySeriesFilter();
        this.applyIndividualFilter();
      });
  }

  applySeriesFilter() {
    const term = this.searchTerm.toLowerCase();
    this.filteredReservations = this.fixedReservations.filter((r: any) => {
      const owner = (r.booking_user || "").toLowerCase();
      const players = (r.courtReservationPlayer || [])
        .map((p: any) => (p.player?.full_name || "").toLowerCase())
        .join(" ");
      return owner.includes(term) || players.includes(term);
    });
  }

  applyIndividualFilter() {
    const userTerm = this.userFilter.toLowerCase();
    const seriesTerm = this.seriesFilter.toLowerCase();
    this.filteredIndividualReservations = this.individualReservations.filter(
      (r: any) => {
        const owner = (r.booking_user || "").toLowerCase();
        const players = (r.courtReservationPlayer || [])
          .map((p: any) => (p.player?.full_name || "").toLowerCase())
          .join(" ");
        const series = (r.fixed_series_id || "").toString().toLowerCase();
        const userMatch =
          !userTerm || owner.includes(userTerm) || players.includes(userTerm);
        const seriesMatch = !seriesTerm || series.includes(seriesTerm);
        return userMatch && seriesMatch;
      }
    );
  }

  deleteSeries(res: any) {
    if (!res.fixed_series_id) return;
    const confirmed = confirm(
      "Are you sure you want to delete this fixed reservation series? This action cannot be undone."
    );
    if (!confirmed) return;
    this.reservationService.deleteFixedSeries(res.fixed_series_id).subscribe({
      next: () => {
        this.messageService.add({
          severity: "success",
          summary: "Deleted",
          detail: "Fixed reservation series deleted",
        });
        this.loadFixedReservations();
      },
      error: () => {
        this.messageService.add({
          severity: "error",
          summary: "Error",
          detail: "Failed to delete fixed reservation series",
        });
      },
    });
  }

  canDelete(res: any): boolean {
    return this.verified || res.user_id === this.currentUser?.id;
  }

  getPlayerNames(res: any): string {
    return (res.courtReservationPlayer || [])
      .map((p: any) => p.player?.full_name)
      .join(', ');
  }

  canModifyPlayers(res: any): boolean {
    return this.verified || res.user_id === this.currentUser?.id;
  }

  canCancel(res: any): boolean {
    return this.canModifyPlayers(res);
  }

  openPlayerDialog(res: any) {
    this.selectedReservation = res;
    this.newPlayer = null;
    this.playerDialogVisible = true;
  }

  getAddablePlayers() {
    if (!this.selectedReservation) return [];
    const existing = new Set(
      (this.selectedReservation.courtReservationPlayer || []).map(
        (p: any) => p.player_id
      )
    );
    return this.allUsers
      .filter((u) => !existing.has(u.id))
      .map((u) => ({ label: `${u.name} ${u.last_name}`, value: u.id }));
  }

  searchPlayers(event: any) {
    const query = event.query.toLowerCase();
    this.playerSuggestions = this.getAddablePlayers().filter((p: any) =>
      p.label.toLowerCase().includes(query)
    );
  }

  addPlayer() {
    if (!this.selectedReservation || !this.newPlayer) return;
    const res = { ...this.selectedReservation };
    const players = [...(res.courtReservationPlayer || [])];
    const playerId = this.newPlayer.value;
    players.push({
      court_reservation_id: res.id,
      player_id: playerId,
      position: players.length,
    });
    res.courtReservationPlayer = players;
    res.player_ids = players.map((p: any) => p.player_id);
    this.reservationService.updateCourtReservation(res).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Added',
          detail: 'Player added',
        });
        this.loadFixedReservations();
        this.playerDialogVisible = false;
        this.newPlayer = null;
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to add player',
        });
      },
    });
  }

  removePlayer(playerId: number) {
    if (!this.selectedReservation || playerId === this.selectedReservation.user_id)
      return;
    const res = { ...this.selectedReservation };
    let players = (res.courtReservationPlayer || []).filter(
      (p: any) => p.player_id !== playerId
    );
    players = players.map((p: any, idx: number) => ({
      court_reservation_id: res.id,
      player_id: p.player_id,
      position: idx,
    }));
    res.courtReservationPlayer = players;
    res.player_ids = players.map((p: any) => p.player_id);
    this.reservationService.updateCourtReservation(res).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Removed',
          detail: 'Player removed',
        });
        this.loadFixedReservations();
        this.playerDialogVisible = false;
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to remove player',
        });
      },
    });
  }

  cancelReservation(res: any) {
    const confirmed = confirm('Are you sure you want to cancel this booking?');
    if (!confirmed) return;
    this.reservationService.deleteCourtReservation(res.id).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Cancelled',
          detail: 'Reservation cancelled',
        });
        this.loadFixedReservations();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to cancel reservation',
        });
      },
    });
  }

  private getCourtName(id: number): string {
    const c = this.courts.find((x) => x.id === id);
    return c ? c.name : id.toString();
  }

  showSeriesInfo() {
    this.infoDialogVisible = true;
  }

  private generateTimes() {
    this.times = [];
    if (!this.club || this.selectedDay === null) return;
    const today = new Date();
    const diff = (7 + this.selectedDay - today.getDay()) % 7;
    const target = new Date(today);
    target.setDate(today.getDate() + diff);
    const intervals = this.computeOpeningIntervals(target);
    for (const interval of intervals) {
      const start = Math.ceil(interval.start);
      const end = Math.floor(interval.end);
      for (let i = start; i < end; i++) {
        this.times.push({
          label: `${i.toString().padStart(2, "0")}:00`,
          value: i,
        });
      }
    }
  }

  onDayChange() {
    this.selectedTime = null;
    this.generateTimes();
  }

  searchUsers(query: string) {
    const q = (query || "").toLowerCase();
    this.filteredUsers = this.allUsers
      .filter(
        (u) =>
          (u.username && u.username.toLowerCase().includes(q)) ||
          `${u.name} ${u.last_name}`.toLowerCase().includes(q)
      )
      .map((u) => u.username);
  }

  selectSuggestion(username: string) {
    this.username = username;
    this.filteredUsers = [];
  }

  async checkAvailability() {
    this.checked = false;
    this.loading = true;
    this.unavailable = [];
    this.slotPrice = 0;
    this.totalPrice = 0;
    if (
      !this.club ||
      !this.selectedCourt ||
      this.selectedDay === null ||
      this.selectedTime === null
    ) {
      this.loading = false;
      return;
    }

    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setMonth(end.getMonth() + this.selectedMonths);

    const reservations = await firstValueFrom(
      this.reservationService.getReservationsForClubDateRange(
        this.club.id,
        start.toISOString().split("T")[0],
        end.toISOString().split("T")[0],
        "Approved"
      )
    );

    const firstDate = new Date(start);
    const diff = (7 + this.selectedDay - firstDate.getDay()) % 7;
    firstDate.setDate(firstDate.getDate() + diff);
    firstDate.setHours(this.selectedTime, 0, 0, 0);

    let total = 0;
    this.slotPrice = this.calculatePrice(new Date(firstDate), this.selectedDuration);
    for (
      let current = new Date(firstDate);
      current < end;
      current.setDate(current.getDate() + 7)
    ) {
      const cur = new Date(current);
      const intervals = this.computeOpeningIntervals(cur);
      const open = intervals.some(
        (i) => this.selectedTime! >= i.start && this.selectedTime! + this.selectedDuration <= i.end
      );
      const conflict = reservations.some((r: any) => {
        if (r.court_id !== this.selectedCourt.id) return false;
        const resStart = new Date(r.reservation_start_time);
        const resEnd = new Date(r.reservation_end_time);
        if (
          resStart.getFullYear() === cur.getFullYear() &&
          resStart.getMonth() === cur.getMonth() &&
          resStart.getDate() === cur.getDate()
        ) {
          const s = resStart.getHours();
          const e = resEnd.getHours();
          const reservationEnd = this.selectedTime! + this.selectedDuration;
          return (this.selectedTime! >= s && this.selectedTime! < e) ||
                 (reservationEnd > s && reservationEnd <= e) ||
                 (this.selectedTime! < s && reservationEnd > e);
        }
        return false;
      });
      if (!open || conflict) {
        this.unavailable.push(new Date(cur));
      }
      total += this.calculatePrice(cur, this.selectedDuration);
    }
    this.totalPrice = total;
    this.checked = true;
    this.loading = false;
  }

  async book() {
    if (
      !this.checked ||
      this.unavailable.length > 0 ||
      !this.club ||
      !this.selectedCourt ||
      this.selectedDay === null ||
      this.selectedTime === null ||
      !this.username
    ) {
      return;
    }

    const name = this.username.trim();
    const user = await firstValueFrom(this.userService.getUserByUsername(name));
    if (!user) {
      this.messageService.add({
        severity: "warn",
        summary: "User not found",
        detail: `Player @${name} not found`,
      });
      return;
    }

    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setMonth(end.getMonth() + this.selectedMonths);
    const firstDate = new Date(start);
    const diff = (7 + this.selectedDay - firstDate.getDay()) % 7;
    firstDate.setDate(firstDate.getDate() + diff);
    firstDate.setHours(this.selectedTime, 0, 0, 0);

    const reservations: any[] = [];
    const fixedSeriesId =
      (globalThis as any).crypto?.randomUUID?.() || this.generateUUID();
    for (
      let current = new Date(firstDate);
      current < end;
      current.setDate(current.getDate() + 7)
    ) {
      const startLocal = new Date(
        current.getTime() - current.getTimezoneOffset() * 60000
      );
      const endLocal = new Date(startLocal.getTime() + this.selectedDuration * 60 * 60 * 1000);
      reservations.push({
        club_id: this.club.id,
        court_id: this.selectedCourt.id,
        user_id: user.id,
        reservation_start_time: startLocal.toISOString(),
        reservation_end_time: endLocal.toISOString(),
        started: false,
        finished: false,
        reservation_status: "Approved",
        is_private: false,
        match_type: "single",
        reservation_type: "non employee",
        sets: [-1, -1, -1, -1, -1, -1],
        fixed_series_id: fixedSeriesId,
        courtReservationPlayer: [
          {
            court_reservation_id: 0,
            player_id: user.id,
            position: 0,
            player: {
              id: user.id,
              full_name: `${user.name} ${user.last_name}`,
            },
          },
        ],
      });
    }

    try {
      await firstValueFrom(
        this.reservationService.reserveSequence(reservations as any)
      );
      this.messageService.add({
        severity: "success",
        summary: "Booked",
        detail: "Fixed slot booked",
      });
      this.username = "";
      this.checked = false;
      this.unavailable = [];
      this.slotPrice = 0;
      this.totalPrice = 0;
    } catch (error) {
      console.error("Failed to book fixed reservation:", error);
      console.error("Error details:", JSON.stringify(error, null, 2));
      this.messageService.add({
        severity: "error",
        summary: "Error",
        detail: "Failed to book court",
      });
    }
  }

  private getHour(ts: any): number {
    if (!ts) return 0;
    if (typeof ts === "string") {
      const parts = ts.split(":");
      const h = parseInt(parts[0], 10) || 0;
      const m = parseInt(parts[1], 10) || 0;
      return h + m / 60;
    }
    if (typeof ts === "object" && "hours" in ts) {
      return ts.hours + (ts.minutes || 0) / 60;
    }
    const num = Number(ts);
    return isNaN(num) ? 0 : num;
  }

  private computeOpeningIntervals(date: Date): { start: number; end: number }[] {
    const dow = date.getDay();
    let intervals = (this.club.clubOpeningHours || [])
      .filter((o: any) => o.day_of_week === dow)
      .map((o: any) => ({
        start: this.getHour(o.start_time),
        end: this.getHour(o.end_time),
      }));
    const exc = (this.club.clubOpeningExceptions || []).find((e: any) => {
      const d = new Date(e.date_);
      return (
        d.getFullYear() === date.getFullYear() &&
        d.getMonth() === date.getMonth() &&
        d.getDate() === date.getDate()
      );
    });
    if (exc) {
      if (exc.is_closed) return [];
      const excStart = this.getHour(exc.start_time);
      const excEnd = this.getHour(exc.end_time);
      intervals = intervals.flatMap((int: any) => {
        if (excEnd <= int.start || excStart >= int.end) return [int];
        const res: { start: number; end: number }[] = [];
        if (excStart > int.start)
          res.push({ start: int.start, end: Math.min(excStart, int.end) });
        if (excEnd < int.end)
          res.push({ start: Math.max(excEnd, int.start), end: int.end });
        return res;
      });
    }
    return intervals;
  }

  private calculatePrice(start: Date, duration: number): number {
    const dow = start.getDay();
    const t = start.getHours() + start.getMinutes() / 60;
    const pricing = (this.club.clubPricingHours || []).find(
      (p: any) =>
        p.day_of_week === dow &&
        t >= this.getHour(p.start_time) &&
        t < this.getHour(p.end_time)
    );
    const hourly = pricing ? pricing.price_rsds : this.club.price_per_hour;
    const bundle = (this.club.clubPricingBundles || []).find(
      (b: any) =>
        b.bundle_hours === duration &&
        (b.day_of_week == null || b.day_of_week === dow) &&
        (!b.start_time || t >= this.getHour(b.start_time)) &&
        (!b.end_time || t < this.getHour(b.end_time))
    );
    const totalHourly = hourly * duration;
    return bundle
      ? Math.min(bundle.price_total_rsds, totalHourly)
      : totalHourly;
  }

  private generateUUID(): string {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0,
        v = c == "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
}

